import { useCallback, useEffect, useRef, useState } from 'react'

import { Input, type InputProps } from '@/shared/ui/input'

function formatInitialValue(value?: number | ''): string {
    if (value === '' || value === undefined || value === null) return ''
    return String(value)
}

function sanitizeDecimal(raw: string): string {
    let sanitized = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.')
    const parts = sanitized.split('.')
    if (parts.length > 2) {
        sanitized = `${parts[0]}.${parts.slice(1).join('')}`
    }
    return sanitized
}

function sanitizeNumeric(raw: string): string {
    return raw.replace(/[^0-9]/g, '')
}

function parseDraft(draft: string, emptyValue: number): number {
    if (draft === '' || draft === '.') return emptyValue
    const parsed = parseFloat(draft)
    return Number.isNaN(parsed) ? emptyValue : parsed
}

export type BufferedNumberInputProps = Omit<InputProps, 'value' | 'defaultValue' | 'onChange' | 'type'> & {
    initialValue?: number | ''
    emptyValue?: number
    debounceMs?: number
    mode?: 'decimal' | 'numeric'
    onDebouncedChange: (value: number) => void
}

export function BufferedNumberInput({
    initialValue = '',
    emptyValue = 0,
    debounceMs = 300,
    mode = 'decimal',
    onDebouncedChange,
    inputSize,
    className,
    ...props
}: BufferedNumberInputProps) {
    const [draft, setDraft] = useState(() => formatInitialValue(initialValue))
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const onDebouncedChangeRef = useRef(onDebouncedChange)
    onDebouncedChangeRef.current = onDebouncedChange

    const scheduleEmit = useCallback(
        (nextDraft: string) => {
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => {
                onDebouncedChangeRef.current(parseDraft(nextDraft, emptyValue))
            }, debounceMs)
        },
        [debounceMs, emptyValue],
    )

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value
        const nextDraft = mode === 'numeric' ? sanitizeNumeric(raw) : sanitizeDecimal(raw)
        setDraft(nextDraft)
        scheduleEmit(nextDraft)
    }

    return (
        <Input
            {...props}
            inputSize={inputSize}
            className={className}
            type='text'
            inputMode={mode === 'numeric' ? 'numeric' : 'decimal'}
            value={draft}
            onChange={handleChange}
        />
    )
}
